// Custom tab-bar glyphs for the Work View, recreated from the design SVG.
// All strokes/fills use `currentColor` so the active tab's text color drives
// the icon color. Each icon renders at a fixed 34×34 on a 20×20 `viewBox`, so
// every tab keeps the same on-screen footprint.

/** Orders — three stacked rows. */
export function OrdersIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="3" width="16" height="2.5" rx="1.25" fill="currentColor" />
      <rect x="2" y="8.75" width="16" height="2.5" rx="1.25" fill="currentColor" />
      <rect x="2" y="14.5" width="10" height="2.5" rx="1.25" fill="currentColor" />
      <circle cx="4.5" cy="4.25" r="1.25" fill="currentColor" opacity="0" />
    </svg>
  );
}

/** Plan — staggered task bars against a faint vertical axis (Gantt-like). */
export function PlanIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Row 1 - starts left, medium width */}
      <rect x="2" y="3.5" width="8" height="2.25" rx="1.125" fill="currentColor" />
      {/* Row 2 - starts mid, longer */}
      <rect x="5" y="8" width="11" height="2.25" rx="1.125" fill="currentColor" />
      {/* Row 3 - starts left, short */}
      <rect x="2" y="12.5" width="5" height="2.25" rx="1.125" fill="currentColor" />
      {/* Row 4 - starts mid-right */}
      <rect x="8" y="17" width="8" height="2.25" rx="1.125" fill="currentColor" />
      {/* Vertical axis line */}
      <line
        x1="2"
        y1="2"
        x2="2"
        y2="19"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        opacity="0.35"
      />
    </svg>
  );
}

/** Manifest — loose items consolidating (arrow) into one solid block. */
export function ManifestIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Loose blocks left - scattered sizes */}
      <rect x="1.5" y="2.5" width="4" height="3" rx="0.6" fill="currentColor" opacity="0.35" />
      <rect x="1.5" y="6.5" width="4" height="2.5" rx="0.6" fill="currentColor" opacity="0.55" />
      <rect x="1.5" y="10" width="4" height="3.5" rx="0.6" fill="currentColor" opacity="0.75" />
      <rect x="1.5" y="14.5" width="4" height="2.5" rx="0.6" fill="currentColor" opacity="0.45" />
      {/* Arrow */}
      <line
        x1="7.5"
        y1="10"
        x2="10.5"
        y2="10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <polyline
        points="9.25,8.25 11.25,10 9.25,11.75"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Consolidated solid block right */}
      <rect x="12.5" y="2.5" width="6" height="15" rx="1" fill="currentColor" />
    </svg>
  );
}

/** Legs — three rows of two-segment dashes. */
export function LegsIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Row 1 - two segments with gap */}
      <rect x="2" y="3.5" width="5" height="2.25" rx="1.125" fill="currentColor" />
      <rect x="9.5" y="3.5" width="8.5" height="2.25" rx="1.125" fill="currentColor" />
      {/* Row 2 - two segments with gap */}
      <rect x="2" y="8" width="7" height="2.25" rx="1.125" fill="currentColor" />
      <rect x="11.5" y="8" width="6.5" height="2.25" rx="1.125" fill="currentColor" />
      {/* Row 3 - two segments with gap */}
      <rect x="2" y="12.5" width="9" height="2.25" rx="1.125" fill="currentColor" />
      <rect x="13.5" y="12.5" width="4.5" height="2.25" rx="1.125" fill="currentColor" />
    </svg>
  );
}

/** Bookings — a perforated two-part ticket. */
export function BookingsIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Ticket body left */}
      <path
        d="M2 6 C2 5 2.75 4.5 3.5 4.5 L9.5 4.5 L9.5 7.5 C8.5 7.5 8.5 8.5 9.5 8.5 L9.5 15.5 L3.5 15.5 C2.75 15.5 2 15 2 14 Z"
        fill="currentColor"
        opacity="0.9"
      />
      {/* Ticket body right */}
      <path
        d="M10.5 4.5 L16.5 4.5 C17.25 4.5 18 5 18 6 L18 14 C18 15 17.25 15.5 16.5 15.5 L10.5 15.5 L10.5 12.5 C11.5 12.5 11.5 11.5 10.5 11.5 L10.5 8.5 C11.5 8.5 11.5 7.5 10.5 7.5 Z"
        fill="currentColor"
        opacity="0.5"
      />
      {/* Perforation dots */}
      <circle cx="10" cy="7.5" r="0.6" fill="white" />
      <circle cx="10" cy="9.5" r="0.6" fill="white" />
      <circle cx="10" cy="11.5" r="0.6" fill="white" />
      <circle cx="10" cy="13.5" r="0.6" fill="white" />
      {/* Lines on left ticket stub */}
      <rect x="3.5" y="8" width="4.5" height="1.25" rx="0.625" fill="white" opacity="0.7" />
      <rect x="3.5" y="10.5" width="3" height="1.25" rx="0.625" fill="white" opacity="0.5" />
    </svg>
  );
}
